import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Edit, Trash, Tag, Percentage } from "@mynaui/icons-react";
import { getPromoCodesByTournament } from "../../api/promoCodes";
import { getTournamentDetail } from "../../api/tournaments";
import { IPromoCode } from "../../models/IPromoCode";
import { PromoCodeDialog, DeletePromoCodeDialog } from "../../components";
import { Button, Card, DescriptionList, EmptyState, IconButton, Loader, PageHeader, Tag as StatusTag } from "../../ui";
import "./styles.scss";

const TournamentPromoCodesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [tournamentName, setTournamentName] = useState("");
  const [promoCodes, setPromoCodes] = useState<IPromoCode[]>([]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<IPromoCode | undefined>(undefined);
  const [deletingPromoCode, setDeletingPromoCode] = useState<{ id: string; code: string } | undefined>(undefined);

  const fetchData = async () => {
    if (!id) return;

    try {
      const [tournamentRes, promoCodesRes] = await Promise.all([
        getTournamentDetail(id),
        getPromoCodesByTournament(id),
      ]);

      setTournamentName(tournamentRes.data.name);
      setPromoCodes(promoCodesRes.data || []);
      document.title = `Promo Codes - ${tournamentRes.data.name}`;
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, [id]);

  const handleDialogClose = (update?: boolean) => {
    setShowCreateDialog(false);
    setEditingPromoCode(undefined);
    if (update) {
      setLoading(true);
      fetchData();
    }
  };

  const handleDeleteClose = (update?: boolean) => {
    setDeletingPromoCode(undefined);
    if (update) {
      setLoading(true);
      fetchData();
    }
  };

  const handleEdit = (promoCode: IPromoCode) => {
    setEditingPromoCode(promoCode);
  };

  const handleDelete = (promoCode: IPromoCode) => {
    setDeletingPromoCode({ id: promoCode.id, code: promoCode.code });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUsageStatus = (promoCode: IPromoCode) => {
    if (!promoCode.maxUsageCount) return `${promoCode.currentUsageCount} uses`;
    const percentage = (promoCode.currentUsageCount / promoCode.maxUsageCount) * 100;
    return `${promoCode.currentUsageCount}/${promoCode.maxUsageCount} (${Math.round(percentage)}%)`;
  };

  return (
    <div className="tournament-promo-codes-page">
      <PageHeader
        back={{ onClick: () => navigate(`/dashboard/tournaments/${id}`), label: "Back" }}
        eyebrow={tournamentName}
        title="Promo Codes"
        subtitle={
          !loading ? `${promoCodes.length} Promo ${promoCodes.length === 1 ? "Code" : "Codes"}` : undefined
        }
        actions={
          !loading ? (
            <Button icon={<Plus size={18} />} onClick={() => setShowCreateDialog(true)}>
              Create Promo Code
            </Button>
          ) : undefined
        }
      />

      {loading ? (
        <Loader minHeight={400} />
      ) : promoCodes.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<Tag />}
            title="No promo codes yet"
            description="Create promo codes to offer discounts during team registration"
            action={
              <Button icon={<Plus size={18} />} onClick={() => setShowCreateDialog(true)}>
                Create Your First Promo Code
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="promo-codes-grid">
          {promoCodes.map((promoCode) => (
            <Card
              key={promoCode.id}
              className={`promo-code-card${!promoCode.isActive ? " is-inactive" : ""}`}
              icon={<Percentage />}
              title={promoCode.code}
              subtitle={promoCode.description || undefined}
              actions={
                <>
                  <IconButton label="Edit" icon={<Edit size={18} />} onClick={() => handleEdit(promoCode)} />
                  <IconButton
                    label="Delete"
                    variant="danger"
                    icon={<Trash size={18} />}
                    onClick={() => handleDelete(promoCode)}
                  />
                </>
              }
              footer={
                <div className="promo-code-card__foot">
                  <StatusTag tone={promoCode.isActive ? "ok" : "neutral"}>
                    {promoCode.isActive ? "Active" : "Inactive"}
                  </StatusTag>
                  <span className="promo-code-card__created">Created: {formatDate(promoCode.createdAt)}</span>
                </div>
              }
            >
              <DescriptionList
                columns={2}
                items={[
                  {
                    label: "Discount",
                    value: (
                      <span className="promo-code-card__discount">
                        {promoCode.discountPercentage
                          ? `${promoCode.discountPercentage}%`
                          : promoCode.discountAmount
                          ? `${promoCode.discountAmount}`
                          : "N/A"}
                      </span>
                    ),
                  },
                  { label: "Valid From", value: formatDate(promoCode.validFrom) },
                  { label: "Valid Until", value: formatDate(promoCode.validUntil) },
                  { label: "Usage", value: getUsageStatus(promoCode) },
                ]}
              />

              {promoCode.maxUsageCount && (
                <div className="promo-code-card__progress">
                  <div
                    className="promo-code-card__progress-fill"
                    style={{
                      width: `${Math.min((promoCode.currentUsageCount / promoCode.maxUsageCount) * 100, 100)}%`,
                    }}
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {(showCreateDialog || editingPromoCode) && (
        <PromoCodeDialog
          tournamentId={id!}
          promoCode={editingPromoCode}
          isOpen={true}
          onClose={handleDialogClose}
        />
      )}

      {deletingPromoCode && (
        <DeletePromoCodeDialog
          promoCodeId={deletingPromoCode.id}
          promoCode={deletingPromoCode.code}
          isOpen={true}
          onClose={handleDeleteClose}
        />
      )}
    </div>
  );
};

export default TournamentPromoCodesPage;

